import { type FormEvent, useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useToast } from '../lib/toast'
import { Avatar, Button, Card, Chip, Field, FormError, Label, PageTitle } from '../components/ui'
import { type League, type Member, api, call, messageOf } from '../lib/api'
import { leagueQuery, meQuery, seasonsQuery } from '../lib/queries'
import { votes } from '../design/tokens'

export const Route = createFileRoute('/l/$slug/league')({ component: LeaguePage })

function LeaguePage() {
  const { slug } = Route.useParams()
  const { data: league } = useSuspenseQuery(leagueQuery(slug))
  const toast = useToast()
  const link = `${window.location.origin}/join/${league.invite_code}`
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      toast('Invite link copied')
    } catch {
      toast("Couldn't copy. Select the link and copy it by hand.", 'bad')
    }
  }
  return (
    <>
      <PageTitle eyebrow="League">{league.name}</PageTitle>

      <Card className="grid gap-3">
        <Label>Invite friends</Label>
        <p className="break-all rounded-field bg-sunken px-3 py-2 font-mono text-[13px]">{link}</p>
        <Button variant="primary" onClick={copy}>Copy invite link</Button>
        <p className="text-[13px] text-muted">Anyone with the link can join. {league.is_admin ? 'You can reset it below if it gets out.' : ''}</p>
      </Card>

      <section className="grid gap-2">
        <Label>{(league.members ?? []).length === 1 ? '1 manager' : `${(league.members ?? []).length} managers`}</Label>
        <ul className="grid gap-1">
          {(league.members ?? []).map((m) => (
            <li key={m.id} className="flex items-center gap-2.5 py-1.5">
              <Avatar src={m.avatar_url} />
              <span className="min-w-0 flex-1 truncate text-[15px]">
                <b className="font-semibold">{m.display_name}</b> <span className="text-muted">@{m.username}</span>
              </span>
              {m.is_admin ? <Chip tone="done">admin</Chip> : league.is_admin && <MakeAdmin league={league} member={m} />}
            </li>
          ))}
        </ul>
      </section>

      {league.current_round?.can_edit_categories && <CategoryEditor league={league} />}
      {league.is_admin && <NextCategoryEditor league={league} />}
      {league.is_admin && <Settings league={league} />}
      <Leave league={league} />
    </>
  )
}

// An admin makes another member an admin. It takes effect at once and
// can't be undone in the app (designs/20261015-admin-handover.md).
function MakeAdmin({ league, member }: { league: League; member: Member }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [sure, setSure] = useState(false)
  const make = useMutation({
    mutationFn: () => call(api.POST('/leagues/{slug}/admins', { params: { path: { slug: league.slug } }, body: { username: member.username } })),
    onSuccess: (l) => {
      qc.setQueryData(leagueQuery(league.slug).queryKey, l)
      toast(`${member.display_name} is an admin now`)
    },
    onError: (e) => toast(messageOf(e), 'bad'),
  })
  if (!sure) return <Button size="sm" onClick={() => setSure(true)}>Make admin</Button>
  return (
    <span className="flex flex-wrap items-center justify-end gap-1.5 text-[13px]">
      <span className="text-muted">Make {member.display_name} an admin? This can't be undone in the app.</span>
      <Button variant="ink" size="sm" busy={make.isPending} onClick={() => make.mutate()}>Yes</Button>
      <Button size="sm" onClick={() => setSure(false)}>No</Button>
    </span>
  )
}

function CategoryEditor({ league }: { league: League }) {
  const qc = useQueryClient()
  const round = league.current_round!
  return (
    <CategoryForm
      key={`now-${round.number}`}
      title={`Round ${round.number} categories`}
      help="Yours to change until the first post lands, then they lock."
      initial={(round.categories ?? []).map((c) => c.name)}
      save={async (names) => {
        await call(api.PUT('/leagues/{slug}/rounds/{number}/categories', { params: { path: { slug: league.slug, number: round.number } }, body: { names } }))
        qc.invalidateQueries({ queryKey: ['league', league.slug] })
      }}
    />
  )
}

// The round after this one, planned ahead (sc-287). The admin is emailed the
// proposal with a link to #next; it becomes the round's categories when it opens.
function NextCategoryEditor({ league }: { league: League }) {
  const qc = useQueryClient()
  const key = ['next-categories', league.slug]
  const next = useQuery({
    queryKey: key,
    queryFn: () => call(api.GET('/leagues/{slug}/next-categories', { params: { path: { slug: league.slug } } })),
    retry: false,
  })
  useEffect(() => {
    if (next.data && window.location.hash === '#next') document.getElementById('next')?.scrollIntoView({ block: 'start' })
  }, [next.data])
  if (!next.data || (next.data.categories ?? []).length === 0) return null
  const plan = next.data
  const opens = new Date(plan.opens_at).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
  return (
    <div id="next" className="scroll-mt-4">
      <CategoryForm
        key={`next-${plan.round_number}`}
        title={`Round ${plan.round_number} categories`}
        help={`Round ${plan.round_number} opens ${opens} with these. Change them any time before then.`}
        initial={plan.categories ?? []}
        save={async (names) => {
          const saved = await call(api.PUT('/leagues/{slug}/next-categories', { params: { path: { slug: league.slug } }, body: { names } }))
          qc.setQueryData(key, saved)
        }}
      />
    </div>
  )
}

function CategoryForm({ title, help, initial, save: persist }: { title: string; help: string; initial: string[]; save: (names: string[]) => Promise<void> }) {
  const toast = useToast()
  const [names, setNames] = useState<string[]>(initial)
  const pool = useQuery({ queryKey: ['category-pool'], queryFn: async () => (await call(api.GET('/category-pool'))).categories ?? [], staleTime: Infinity })
  const save = useMutation({
    mutationFn: () => persist(names),
    onSuccess: () => toast('Categories saved'),
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <Label>{title}</Label>
      <p className="text-[13.5px] text-muted">{help}</p>
      <FormError>{save.isError && messageOf(save.error)}</FormError>
      {names.map((n, i) => (
        <Field key={i} label={`Category ${i + 1}`} required maxLength={40} list="category-pool" value={n} onChange={(e) => setNames((s) => s.map((x, j) => (j === i ? e.target.value : x)))} />
      ))}
      <datalist id="category-pool">{(pool.data ?? []).map((c) => <option key={c} value={c} />)}</datalist>
      <Button type="submit" variant="ink" busy={save.isPending}>Save categories</Button>
    </form>
  )
}

function Settings({ league }: { league: League }) {
  const qc = useQueryClient()
  const toast = useToast()
  // The current season: its name is editable here, and "Rounds this season"
  // extends it while it runs (designs/20261017-seasons.md §3).
  const seasons = useQuery(seasonsQuery(league.slug))
  const season = seasons.data?.[seasons.data.length - 1]
  const [f, setF] = useState({
    name: league.name,
    season_name: '',
    season_rounds: String(league.season_rounds),
    submission_cap: league.submission_cap == null ? '' : String(league.submission_cap),
    vote_budget: String(league.vote_budget),
  })
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }))
  const seasonName = f.season_name || season?.name || ''
  const save = useMutation({
    mutationFn: async () => {
      const l = await call(api.PATCH('/leagues/{slug}', {
        params: { path: { slug: league.slug } },
        body: {
          name: f.name,
          season_rounds: Number(f.season_rounds),
          submission_cap: f.submission_cap === '' ? undefined : Number(f.submission_cap),
          vote_budget: Number(f.vote_budget),
          vote_max_per_submission: league.vote_max_per_submission ?? undefined,
        },
      }))
      if (season && seasonName.trim() && seasonName.trim() !== season.name) {
        await call(api.PATCH('/leagues/{slug}/seasons/{season}', { params: { path: { slug: league.slug, season: season.number } }, body: { name: seasonName.trim() } }))
      }
      return l
    },
    onSuccess: (l) => {
      qc.setQueryData(leagueQuery(league.slug).queryKey, l)
      qc.invalidateQueries({ queryKey: ['league', league.slug, 'seasons'] })
      qc.invalidateQueries({ queryKey: meQuery.queryKey })
      toast('Settings saved')
    },
  })
  const rotate = useMutation({
    mutationFn: () => call(api.POST('/leagues/{slug}/invite/rotate', { params: { path: { slug: league.slug } } })),
    onSuccess: (l) => {
      qc.setQueryData(leagueQuery(league.slug).queryKey, l)
      toast('Old link no longer works')
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }
  return (
    <form onSubmit={submit} className="grid gap-3 border-t border-line pt-4">
      <Label>Settings</Label>
      <FormError>{save.isError && messageOf(save.error)}</FormError>
      <Field label="League name" required minLength={2} maxLength={60} value={f.name} onChange={set('name')} />
      {season && <Field label={`Season ${season.number} name`} required maxLength={60} value={seasonName} onChange={set('season_name')} />}
      <div className="grid grid-cols-2 gap-3">
        <Field
          label={season?.state === 'complete' ? 'Rounds next season' : 'Rounds this season'}
          type="number" inputMode="numeric" min={1} max={52} required value={f.season_rounds} onChange={set('season_rounds')}
          help={season?.state === 'complete' ? 'The default when you start it.' : `Extends ${season?.name ?? 'the season'}; also the default for the next one.`}
        />
        <Field label={`${votes(2)[0].toUpperCase()}${votes(2).slice(1)} per category`} type="number" inputMode="numeric" min={1} max={50} required value={f.vote_budget} onChange={set('vote_budget')} />
      </div>
      <Field label="Posts allowed per category" type="number" inputMode="numeric" min={1} max={100} placeholder="Unlimited" value={f.submission_cap} onChange={set('submission_cap')} help="Per manager. Leave empty for no limit." />
      <Button type="submit" variant="ink" busy={save.isPending}>Save settings</Button>
      <Button size="sm" busy={rotate.isPending} onClick={() => rotate.mutate()} className="justify-self-start">Reset the invite link</Button>
    </form>
  )
}

function Leave({ league }: { league: League }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [sure, setSure] = useState(false)
  const lastAdmin = league.is_admin && (league.members ?? []).filter((m) => m.is_admin).length === 1 && (league.members ?? []).length > 1
  const leave = useMutation({
    mutationFn: () => call(api.DELETE('/leagues/{slug}/members/me', { params: { path: { slug: league.slug } } })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meQuery.queryKey })
      navigate({ to: '/' })
    },
  })
  return (
    <div className="grid gap-2 border-t border-line pt-4">
      <FormError>{leave.isError && messageOf(leave.error)}</FormError>
      {sure ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-[14.5px]">
            Leave {league.name}? Your past posts and points stay.
            {lastAdmin && " You're its only admin: nobody will run the league until we make someone admin."}
          </span>
          <Button variant="danger" size="sm" busy={leave.isPending} onClick={() => leave.mutate()}>Yes, leave</Button>
          <Button size="sm" onClick={() => setSure(false)}>Stay</Button>
        </div>
      ) : (
        <Button variant="danger" size="sm" onClick={() => setSure(true)} className="justify-self-start">Leave league</Button>
      )}
    </div>
  )
}
